import React, {useContext, useEffect} from "react";
import {useLocation} from "react-router-dom";

import carbonImage from "../../../assets/images/solutions/GasScrubbers.png";
import CompanyCarousel from "../../../components/carousel";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import * as S from "./styles";
import GetInTouch from "../../../components/getInTouch";
import Questions from "../../../components/questions";
import Breadcrumbs from "../../../components/breadcrumbs";
import {GlobalContext} from "../../../context/global/globalContext";

function Gas() {
  const {setBreadcrumbs} = useContext(GlobalContext);
  const location = useLocation();

  useEffect(() => {
    setBreadcrumbs([
      {label: "Home", path: "/"},
      {label: "Technologies", path: "/technologies"},
      {label: "Gas scrubbers", path: "/technologies/gas"},
    ]);
  }, [location, setBreadcrumbs]);

  const questionsData = [
    {
      question: "Do you offer packed bed or venturi scrubbers?",
      answer: "Both, depending on whether you're removing gases or particles.",
    },
    {
      question: "What contaminants can your scrubbers handle?",
      answer: "Acids, VOCs, ammonia, fine dust, and more.",
    },
    {
      question: "Can they be skid-mounted?",
      answer: "Yes, for smaller flows or pilot applications, we offer skid-mounted units.",
    },
  ];

  return (
    <>
      <Header />
      <S.Container>
        <Breadcrumbs />

        <S.MainHeading>Gas Scrubbers</S.MainHeading>

        <S.TwoColumnSection>
          <S.Column>
            <S.Paragraph>
              Gas scrubbers are systems designed to remove harmful substances from industrial
              exhaust gases. These systems work by bringing polluted air or gas into contact with a
              washing liquid that absorbs contaminants such as acid gases, amines, and organic
              pollutants—effectively solving emission and odour problems.
            </S.Paragraph>
            <S.Paragraph>
              With over 35 years of experience and references worldwide, our gas scrubbers are built
              for efficiency, low maintenance, and resistance to corrosion and heat. Thanks to their
              open, clog-free designs, our gas scrubbers ensure long-lasting performance and minimal
              downtime, even in harsh industrial environments.
            </S.Paragraph>
          </S.Column>
          <S.ImageContainer>
            <img src={carbonImage} alt="Carbon Scrubber Equipment" />
          </S.ImageContainer>
        </S.TwoColumnSection>

        <S.Section>
          <S.SectionTitle>How Gas Scrubbers Work</S.SectionTitle>
          <S.Paragraph>
            Gas scrubbers operate by introducing contaminated gas into a column where it comes into
            contact with a scrubbing liquid. This liquid, often combined with specific reagents,
            absorbs and neutralizes pollutants such as acid gases and other unwanted compounds. Our
            scrubbers feature advanced control mechanisms to ensure high removal efficiency and
            stable operation, with options for monitoring pH, conductivity, density, and other
            process parameters.
          </S.Paragraph>
          <S.Paragraph>
            Our scrubbers are suitable for a wide range of applications, effectively handling
            corrosive gases, hot exhaust streams, and dust-laden emissions. Common use cases include
            the removal of:
          </S.Paragraph>
          <S.List>
            <S.BulletItem color="black">
              H₂S – removal in biogas upgrading and sulfur recovery units
            </S.BulletItem>
            <S.BulletItem color="black">
              H₂SO₄ mist – removal in sulfuric acid production or chemical processing plants
            </S.BulletItem>
            <S.BulletItem color="black">
              HCl – removal from metal pickling lines, incineration gas, or chemical manufacturing
              exhaust
            </S.BulletItem>
            <S.BulletItem color="black">
              NH₃ – removal in fertilizer production, wastewater treatment, or animal rendering
              plants
            </S.BulletItem>
            <S.BulletItem color="black">
              VOCs – removal in pharmaceutical, paint, and solvent-based industries
            </S.BulletItem>
            <S.BulletItem color="black">
              SO₂ – removal in flue gas treatment from combustion or ore roasting operations
            </S.BulletItem>
          </S.List>
        </S.Section>

        <S.TwoColumnSection>
          <S.Column>
            <S.SectionTitle>Process Design</S.SectionTitle>
            <S.List>
              <S.BulletItem color="blue">Temperature Range: -10 to 200°C</S.BulletItem>
              <S.BulletItem color="blue">Flow Rates: Designed for 500 to 80,000 Nm³/h</S.BulletItem>
              <S.BulletItem color="blue">Operating Pressure: -50 to +75 mbar</S.BulletItem>
              <S.BulletItem color="blue">Pressure Drop: 10 – 40 mbar</S.BulletItem>
              <S.BulletItem color="blue">Efficiency: up to 99.9% removal efficiency</S.BulletItem>
            </S.List>
          </S.Column>
          <S.Column>
            <S.SectionTitle>Materials</S.SectionTitle>
            <S.List>
              <S.BulletItem color="blue">Carbon steel</S.BulletItem>
              <S.BulletItem color="blue">Stainless steel (316L, 304L)</S.BulletItem>
              <S.BulletItem color="blue">FRP, HDPE, and PP</S.BulletItem>
              <S.BulletItem color="blue">linings of PP or PTFE in acidic enviroments</S.BulletItem>
            </S.List>
          </S.Column>
        </S.TwoColumnSection>

        <S.QuestionsSpacing>
          <Questions items={questionsData} />
        </S.QuestionsSpacing>

        <S.ContainerTitles>
          <S.MiniTitle>Our Partners</S.MiniTitle>
          <S.Title>
            We partner with industry leaders to create clean alternatives to traditional industrial
            processes
          </S.Title>
        </S.ContainerTitles>

        <CompanyCarousel />
        <GetInTouch />
      </S.Container>
      <Footer />
    </>
  );
}

export default Gas;
